import type { CaseShowcaseCardDto } from '../dto/case.dto';

import { genderDisplayText } from '@/shared/enums/gender';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function pickName(value: unknown): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value;
  if (!isRecord(value)) return '';
  for (const key of ['name', 'title', 'label']) {
    const field = value[key];
    if (typeof field === 'string' && field) return field;
    if (field != null && typeof field !== 'object') return String(field);
  }
  return '';
}

function firstPick(obj: Record<string, unknown> | null | undefined, keys: string[]): string {
  if (!obj) return '';
  for (const key of keys) {
    const value = pickName(obj[key]);
    if (value) return value;
  }
  return '';
}

function genderText(raw: unknown): string {
  return genderDisplayText(raw);
}

function extractArchive(row: unknown): Record<string, unknown> | null {
  if (!isRecord(row)) return null;
  const raw = row.extend ?? row.case_maindata_archive ?? row.caseMaindataArchive ?? row.archive ?? row.case_archive ?? row.caseMaindataArchiveRecord;
  const archive = Array.isArray(raw) ? raw[0] : raw;
  return isRecord(archive) ? archive : null;
}

function buildShowcaseCard(row: unknown): CaseShowcaseCardDto {
  if (!isRecord(row)) {
    return {
      id: 0,
      name: '',
      genderText: '',
      ageText: '',
      indication: '',
      treatmentPlan: '',
      bodypart: '',
    };
  }

  const archive = extractArchive(row);
  const name = (archive && typeof archive.name === 'string' ? archive.name : '') || (typeof row.name === 'string' ? row.name : '');

  return {
    id: Number(row.id) > 0 ? Number(row.id) : 0,
    name,
    genderText: genderText(archive?.gender),
    ageText: archive?.age != null && archive.age !== '' ? String(archive.age) : '',
    indication: archive ? firstPick(archive, ['indication', 'ga_indication', 'indicationInfo']) : '',
    treatmentPlan: archive ? firstPick(archive, ['treatment_plan', 'ga_treatment_plan', 'treatmentPlan']) : '',
    bodypart: archive ? firstPick(archive, ['bodypart', 'medical_advice_bodypart']) : '',
  };
}

export class CaseHomeCardMapper {
  static toShowcaseCard(row: unknown): CaseShowcaseCardDto {
    return buildShowcaseCard(row);
  }

  static toShowcaseCardList(rows: unknown[]): CaseShowcaseCardDto[] {
    return (Array.isArray(rows) ? rows : []).map(CaseHomeCardMapper.toShowcaseCard).filter(item => item.id > 0);
  }
}
